import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography, Tooltip, Avatar } from "@mui/material";
import { FiRefreshCw, FiCalendar, FiChevronRight } from "react-icons/fi";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useNavigate } from "react-router-dom";
import { getChildTasks } from "../../../services/todoTask.service";
import { PRIORITY } from "../../../constant/constant";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const C = {
  teal:        "#0D9488",
  tealDark:    "#0F766E",
  tealLight:   "#F0FDFA",
  tealBorder:  "#99F6E4",
  tealSoft:    "#CCFBF1",
  orange:      "#EA580C",
  orangeLight: "#FFF7ED",
  orangeBorder:"#FED7AA",
  slate:       "#64748B",
  slateLight:  "#F8FAFC",
  slateBorder: "#E2E8F0",
  text:        "#0F172A",
  textSub:     "#475569",
  textMuted:   "#94A3B8",
  border:      "#E8EDF2",
};

const PRIORITY_CONFIG = {
  [PRIORITY.HIGH]:   { label: "High",   dot: "#EF4444", bg: "#FEF2F2", text: "#DC2626" },
  [PRIORITY.MEDIUM]: { label: "Medium", dot: "#F59E0B", bg: "#FFFBEB", text: "#B45309" },
  [PRIORITY.LOW]:    { label: "Low",    dot: "#10B981", bg: "#ECFDF5", text: "#059669" },
};

const STATUS_LABEL = { 1: "To Do", 2: "In Progress", 3: "Completed" };
const STATUS_COLOR = { 1: "#64748B", 2: "#F59E0B", 3: "#10B981" };
const STATUS_BG    = { 1: "#F1F5F9", 2: "#FFFBEB", 3: "#ECFDF5" };

// Decode HTML entities and strip tags to plain text
const htmlToPlain = (html) => {
  if (!html) return "";
  const txt = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
  return txt;
};

const getCronFires = (start, end) => {
  if (!start || !end) return [];
  const s = dayjs(start).startOf("day");
  const e = dayjs(end).startOf("day");
  if (!s.isValid() || !e.isValid() || e.isBefore(s)) return [];
  const fires = [];
  let cur = s.startOf("month");
  while (cur.isSameOrBefore(e, "month")) {
    const first = cur.date(1);
    if (first.isSameOrAfter(s, "day") && first.isSameOrBefore(e, "day")) fires.push(first.clone());
    cur = cur.add(1, "month");
  }
  return fires;
};

const taskForDate = (children, fireDate) =>
  children.find((c) => {
    const ref = c.cronScheduleDate ? dayjs(c.cronScheduleDate) : dayjs(c.createdAt);
    return ref.year() === fireDate.year() && ref.month() === fireDate.month();
  });

const initials = (name) =>
  (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

const RecurringSpawnsTab = ({ task }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [glowId, setGlowId] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (!task?._id) return;
    setLoading(true);
    getChildTasks(task._id)
      .then((res) => { if (res?.error === false) setChildren(res.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [task?._id]);

  const fires = getCronFires(task?.recurringStartDate, task?.recurringEndDate);
  const today = dayjs().startOf("day");

  const handleChipClick = (child) => {
    if (!child) return;
    const el = cardRefs.current[child._id];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setGlowId(child._id);
      setTimeout(() => setGlowId(null), 2200);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Box sx={{ display: "inline-block", width: 22, height: 22, borderRadius: "50%", border: `2.5px solid ${C.slateBorder}`, borderTopColor: C.teal, animation: "spin 0.7s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }} />
        <Typography sx={{ mt: 1.5, fontSize: "12px", color: C.textMuted, fontFamily: "'Inter', sans-serif" }}>
          Loading spawned tasks…
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Schedule chips ─────────────────────────────────────────────────── */}
      {fires.length > 0 && (
        <Box sx={{ mb: 2.5, p: 2, bgcolor: C.slateLight, borderRadius: "12px", border: `1px solid ${C.slateBorder}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.2}>
            <Typography sx={{ fontSize: "10px", fontWeight: 800, color: C.slate, textTransform: "uppercase", letterSpacing: "0.09em" }}>
              Schedule — {fires.length} occurrence{fires.length > 1 ? "s" : ""}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {[
                { color: C.tealDark,  label: "Spawned" },
                { color: C.orange,    label: "Pending" },
                { color: C.slate,     label: "Upcoming" },
              ].map((l) => (
                <Stack key={l.label} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: l.color }} />
                  <Typography sx={{ fontSize: "10px", color: C.textMuted, fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>{l.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {fires.map((fire, i) => {
              const child = taskForDate(children, fire);
              const isPast = fire.isBefore(today);

              let bg, border, textColor;
              if (child)        { bg = C.tealLight;    border = C.tealBorder;    textColor = C.tealDark; }
              else if (isPast)  { bg = C.orangeLight;  border = C.orangeBorder;  textColor = C.orange; }
              else              { bg = "#fff";          border = C.slateBorder;   textColor = C.slate; }

              return (
                <Tooltip key={i} title={child ? `${child.title} — click to highlight` : isPast ? "No task created for this date" : "Not yet due"} placement="top" arrow disableInteractive>
                  <Box
                    onClick={() => handleChipClick(child)}
                    sx={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      px: "10px", py: "5px", borderRadius: "20px",
                      bgcolor: bg, border: `1.5px solid ${border}`,
                      cursor: child ? "pointer" : "default",
                      transition: "all 0.15s", userSelect: "none",
                      ...(child && { "&:hover": { bgcolor: C.tealSoft, borderColor: C.teal, transform: "translateY(-1px)", boxShadow: `0 3px 8px ${C.teal}20` } }),
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: textColor, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: textColor, fontFamily: "'Inter', sans-serif", lineHeight: 1 }}>
                      {fire.format("D MMM YYYY")}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Child task cards ───────────────────────────────────────────────── */}
      {children.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: C.tealLight, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
            <FiRefreshCw size={22} color={C.teal} />
          </Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: C.text, fontFamily: "'Inter', sans-serif" }}>No tasks spawned yet</Typography>
          <Typography sx={{ fontSize: "12px", color: C.textMuted, mt: 0.5, fontFamily: "'Inter', sans-serif" }}>
            Cron auto-creates tasks on the 1st of each month within the recurring period.
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Header row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 20, height: 20, borderRadius: "6px", bgcolor: C.tealLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FiRefreshCw size={11} color={C.teal} />
              </Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 800, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'Inter', sans-serif" }}>
                {children.length} Spawned Task{children.length > 1 ? "s" : ""}
              </Typography>
            </Stack>
          </Stack>

          {/* Scrollable card list */}
          <Box sx={{ maxHeight: 460, overflowY: "auto", pr: 0.5, scrollbarWidth: "thin", "&::-webkit-scrollbar": { width: "4px" }, "&::-webkit-scrollbar-thumb": { background: C.slateBorder, borderRadius: "2px" } }}>
            <Stack spacing={1.4}>
              {children.map((child) => {
                const p = PRIORITY_CONFIG[child.priority] || PRIORITY_CONFIG[PRIORITY.LOW];
                const schedDate = child.cronScheduleDate ? dayjs(child.cronScheduleDate) : dayjs(child.createdAt);
                const isGlowing = glowId === child._id;
                const descPlain = htmlToPlain(child.description);

                return (
                  <Box
                    key={child._id}
                    ref={(el) => { cardRefs.current[child._id] = el; }}
                    onClick={() => navigate(`/taskDetails?taskId=${child._id}`)}
                    sx={{
                      position: "relative",
                      bgcolor: "#fff",
                      borderRadius: "12px",
                      border: "1.5px solid",
                      borderColor: isGlowing ? C.teal : C.border,
                      boxShadow: isGlowing
                        ? `0 0 0 4px ${C.teal}22, 0 6px 20px ${C.teal}18`
                        : "0 1px 4px rgba(15,23,42,0.06)",
                      cursor: "pointer",
                      overflow: "hidden",
                      transition: "border-color 0.25s, box-shadow 0.25s, transform 0.15s",
                      animation: isGlowing ? "glowPulse 1s ease-in-out 2" : "none",
                      "@keyframes glowPulse": {
                        "0%,100%": { boxShadow: `0 0 0 4px ${C.teal}22, 0 6px 20px ${C.teal}18` },
                        "50%":     { boxShadow: `0 0 0 8px ${C.teal}30, 0 10px 28px ${C.teal}28` },
                      },
                      "&:hover": { borderColor: C.teal, boxShadow: `0 6px 18px ${C.teal}16`, transform: "translateY(-1px)" },
                    }}
                  >
                    {/* Teal left stripe */}
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3.5px", bgcolor: C.teal, borderRadius: "12px 0 0 12px" }} />

                    <Box sx={{ pl: "16px", pr: 1.5, pt: 1.3, pb: 1.3 }}>
                      {/* Row 1: task no + cron badge — right: status */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.7}>
                        <Stack direction="row" spacing={0.7} alignItems="center">
                          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: C.teal, fontFamily: "'Inter', monospace", letterSpacing: "0.05em" }}>
                            #{child.taskNo || "—"}
                          </Typography>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", px: "6px", py: "2px", borderRadius: "20px", bgcolor: C.tealLight, border: `1px solid ${C.tealBorder}` }}>
                            <FiRefreshCw size={7} color={C.teal} />
                            <Typography sx={{ fontSize: "9px", fontWeight: 800, color: C.teal, fontFamily: "'Inter', sans-serif", letterSpacing: "0.05em" }}>CRON</Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <Box sx={{ px: "8px", py: "2px", borderRadius: "20px", bgcolor: STATUS_BG[child.status] || "#F1F5F9", border: `1px solid ${STATUS_COLOR[child.status] || C.textMuted}35` }}>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: STATUS_COLOR[child.status] || C.textMuted, fontFamily: "'Inter', sans-serif" }}>
                              {STATUS_LABEL[child.status] || "—"}
                            </Typography>
                          </Box>
                          <FiChevronRight size={13} color={C.textMuted} />
                        </Stack>
                      </Stack>

                      {/* Title — 1 line truncated */}
                      <Tooltip title={child.title || ""} placement="top" arrow disableInteractive>
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: C.text, lineHeight: 1.4, mb: descPlain ? 0.35 : 0.9, fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {child.title}
                        </Typography>
                      </Tooltip>

                      {/* Description — 1 line truncated */}
                      {descPlain && (
                        <Tooltip title={descPlain} placement="top" arrow disableInteractive>
                          <Typography sx={{ fontSize: "11.5px", color: C.textMuted, lineHeight: 1.45, mb: 0.9, fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {descPlain}
                          </Typography>
                        </Tooltip>
                      )}

                      {/* Divider */}
                      <Box sx={{ height: "1px", bgcolor: C.border, mx: -0.5, mb: 0.9 }} />

                      {/* Footer: priority + dates */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "7px", py: "2px", borderRadius: "20px", bgcolor: p.bg }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: p.dot }} />
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: p.text, fontFamily: "'Inter', sans-serif" }}>{p.label}</Typography>
                        </Box>

                        <Stack direction="row" spacing={1.4} alignItems="center">
                          <Tooltip title="Recurring schedule date" placement="top" arrow disableInteractive>
                            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ cursor: "default" }}>
                              <FiRefreshCw size={9} color={C.teal} />
                              <Typography sx={{ fontSize: "10.5px", color: C.teal, fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                                {schedDate.format("DD MMM YYYY")}
                              </Typography>
                            </Stack>
                          </Tooltip>
                          {child.actualDeadlineDate && (
                            <Tooltip title="Deadline" placement="top" arrow disableInteractive>
                              <Stack direction="row" alignItems="center" spacing={0.4} sx={{ cursor: "default" }}>
                                <FiCalendar size={9} color={C.textMuted} />
                                <Typography sx={{ fontSize: "10.5px", color: C.textMuted, fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                                  {dayjs(child.actualDeadlineDate).format("DD MMM YYYY")}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RecurringSpawnsTab;
